import { applyHelmertTransform } from '@/logic/adjustments';
import { isCoordinate } from '@/utils/fileParser';
import { WEIGHT, type WeightStatus } from '@/logic/weights';
import type { FitSnapshot } from '@/types/adjustmentReview';

/** Pan and zoom of one pane, relative to its fitted frame. */
export interface ViewState {
  zoom: number;
  panX: number;
  panY: number;
}

export const DEFAULT_VIEW: ViewState = { zoom: 1, panX: 0, panY: 0 };

export const MIN_ZOOM = 0.5;
export const MAX_ZOOM = 64;
/** Multiplicative step of the zoom buttons and keys. */
export const ZOOM_STEP = 1.25;

/** Residual vector exaggeration; 'auto' sizes the largest vector to a readable length. */
export type Exaggeration = 'auto' | number;

export const EXAGGERATION_OPTIONS: ReadonlyArray<{
  value: Exaggeration;
  label: string;
}> = [
  { value: 'auto', label: 'Auto' },
  { value: 1, label: '1× (true scale)' },
  { value: 10, label: '10×' },
  { value: 25, label: '25×' },
  { value: 50, label: '50×' },
  { value: 100, label: '100×' },
];

export const exaggerationLabel = (value: Exaggeration): string =>
  value === 'auto' ? 'Auto' : `${value}×`;

/**
 * Map weight shortcuts (with focus in the map), as documented in the user
 * guide: U Unity, F Free, X Fixed, I Ignore.
 */
export const WEIGHT_SHORTCUTS: Readonly<Record<WeightStatus, string>> = {
  unity: 'U',
  fixed: 'X',
  free: 'F',
  ignore: 'I',
};

const SHORTCUT_WEIGHTS: Readonly<Record<string, number>> = Object.fromEntries(
  (Object.keys(WEIGHT_SHORTCUTS) as WeightStatus[]).map((status) => [
    WEIGHT_SHORTCUTS[status].toLowerCase(),
    WEIGHT[status],
  ])
);

/** The weight a map shortcut key assigns, if any. */
export const weightForShortcut = (key: string): number | undefined =>
  SHORTCUT_WEIGHTS[key.toLowerCase()];

/** Short distance-unit suffix for the scale bar; units are labels only. */
export const unitSuffix = (units: string | undefined): string => {
  switch (units) {
    case 'm':
      return 'm';
    case 'international ft':
      return 'ft';
    case 'US survey ft':
      return 'US ft';
    default:
      return '';
  }
};

/** Transform the original strings from this fit's input snapshot. */
export const transformedFitPoints = (fit: FitSnapshot | null | undefined) => {
  if (!fit || fit.solution.error) return [];
  return fit.pointPairs.flatMap((pair) => {
    if (![pair.n1, pair.e1].every(isCoordinate)) return [];
    const transformed = applyHelmertTransform(
      parseFloat(pair.n1),
      parseFloat(pair.e1),
      fit.solution
    );
    return [
      {
        id: pair.id,
        point: pair.sys1Point,
        n: transformed.n2,
        e: transformed.e2,
      },
    ];
  });
};
